import React from "react";

import { Impulse } from "~/tools/impulse";
import { max } from "~/tools/max";
import { round } from "~/tools/round";
import { usePermanent } from "~/tools/use-permanent";

import { useTimestampCursor } from "../TimestampCursor";

import { TimelineContextProvider, type TimelineContextValue, type TimelineProviderProps } from "./_context";

const PX_REFRESH_RATE = 1 / 8; // means that every pixel will be updated 8 times per second
const MAX_REFRESH_FPS = 60;

export const TimelineProvider: React.FC<React.PropsWithChildren<TimelineProviderProps>> = ({
	frozen,
	stageWidth,
	timestamp,
	timeRange,
	eventBucketsState,
	lineState,
	formatTimestamp,
	children,
}) => {
	const timestampCursor = useTimestampCursor();
	const frozenTimeRange = usePermanent(() => Impulse(timeRange.toSpecific()));

	const value = React.useMemo<TimelineContextValue>(() => {
		const refreshInterval = max(
			1000 / MAX_REFRESH_FPS,
			round((timeRange.duration() / stageWidth) * PX_REFRESH_RATE, 0),
		);

		return {
			frozen,
			timestamp,
			timeRange,
			frozenTimeRange,
			eventBucketsState,
			lineState,
			timestampCursor,
			refreshInterval,
			stageWidth,
			formatTimestamp,
		};
	}, [
		frozen,
		timestamp,
		timeRange,
		frozenTimeRange,
		eventBucketsState,
		lineState,
		timestampCursor,
		stageWidth,
		formatTimestamp,
	]);

	return <TimelineContextProvider value={value}>{children}</TimelineContextProvider>;
};
